import { useAxios } from '@/hooks/useAxios';
import { getQueryKey, useGetUserKey } from '@/hooks/useQueryKeys';
import { UseExtendInfiniteQueryOptions, useInfiniteQuery } from '@/utils/api';

export interface CapabilityParameter {
  name: string;
  description: string;
  default: string | null;
  required: boolean;
  type: string;
  options?: string[];
}

export interface Capability {
  name: string;
  title?: string;
  target: string;
  description?: string;
  version?: string;
  executor: string;
  surface: string;
  category?: string;
  runs_on?: string;
  parameters?: CapabilityParameter[];
  largeArtifact?: boolean; // Indicates if this capability generates large output
}

interface UseCapabilitiesProps {
  name?: string;
  target?: string;
  surface?: string;
  category?: string;
  runs_on?: string;
}

export type UseCapabilitiesOptions = UseExtendInfiniteQueryOptions<any>; // eslint-disable-line @typescript-eslint/no-explicit-any

export const useCapabilities = (
  filters?: UseCapabilitiesProps,
  options?: UseCapabilitiesOptions
) => {
  const axios = useAxios();

  const response = useInfiniteQuery<Capability[]>({
    ...options,
    defaultErrorMessage: 'Failed to fetch capabilities',
    retry: false,
    queryKey: useGetUserKey(getQueryKey.getCapabilities(filters)),
    queryFn: async ({ pageParam }) => {
      const params: Record<string, string> = {};

      if (filters?.name) params.name = filters.name;
      if (filters?.target) params.target = filters.target;
      if (filters?.surface) params.surface = filters.surface;
      if (filters?.category) params.category = filters.category;
      if (filters?.runs_on) params.runs_on = filters.runs_on;
      if (pageParam) params.cursor = pageParam as string;

      const { data } = await axios({
        method: 'GET',
        url: '/capabilities',
        params,
      });

      // Handle both array response and object with capabilities array
      const capabilities: Capability[] = Array.isArray(data)
        ? data
        : data.capabilities || data || [];

      return {
        data: capabilities,
        offset: Array.isArray(data) ? undefined : data.offset,
      };
    },
    initialPageParam: undefined,
    getNextPageParam: lastPage => lastPage.offset,
  });

  const processedData: Capability[] = response.data
    ? response.data.pages.map(page => page.data).flat()
    : [];

  return {
    ...response,
    data: processedData,
  };
};
